import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { adminRequest } from "../../../utils/requestMethods";
import { BASE_URL } from "../../../utils/config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomForm from "../../../components/CustomForm/CustomForm";
import Loader from "../../../components/Loader/Loader";

const EditStudent = () => {
  const location = useLocation();
  const activeURL = location.pathname.split("/")[3];
  const initialFormData = {
    fullName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    address: "",
    batch: "",
    enrolledProgram: "",
    enrolledSemester: "",
    studentSection: "",
    enrollmentSetupId: "",
    enrollmentDate: "",
    parentFullName: "",
    parentEmail: "",
    parentPhoneNumber: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [data, setData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [semester, setSemester] = useState([]);
  const [program, setProgram] = useState([]);
  const [enrollmentBatch, setEnrollmentBatch] = useState([]);
  const [sections, setSections] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/course/getNameAndId`,
        );
        setProgram(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchPrograms();
  }, []);

  useEffect(() => {
    const fetchSemesters = async () => {
      if (!formData.enrolledProgram) return;
      try {
        const response = await adminRequest.post(
          `${BASE_URL}/course/getSemestersById`,
          { id: formData.enrolledProgram },
        );
        setSemester(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSemesters();
  }, [formData.enrolledProgram]);

  useEffect(() => {
    const fetchSections = async () => {
      if (!formData.enrolledSemester) return;
      try {
        const response = await adminRequest.post(
          `${BASE_URL}/section/list/by/curriculum`,
          { curriculumId: formData.enrolledSemester },
        );
        setSections(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchSections();
  }, [formData.enrolledSemester]);

  useEffect(() => {
    const fetchEnrollmentSetup = async () => {
      try {
        const response = await adminRequest.get(
          `${BASE_URL}/enrollmentSetup/list`,
        );
        setEnrollmentBatch(response.data.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchEnrollmentSetup();
  }, []);

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const response = await adminRequest.post(`${BASE_URL}/student/view`, {
          uuid: `${activeURL}`,
        });
        setData(response.data);
        setFormData({
          fullName: response.data.data.fullName,
          email: response.data.data.email,
          phoneNumber: response.data.data.phoneNumber,
          dateOfBirth: response.data.data.dateOfBirth,
          address: response.data.data.address,
          batch: response.data.data.batch,
          enrolledProgram: response.data.data.enrolledProgram,
          enrolledSemester: response.data.data.enrolledSemester,
          enrollmentSetupId: response.data.data.enrollmentSetupId,
          enrollmentDate: response.data.data.enrollmentDate,
          studentSection: response.data.data.sections?.[0] || "",
          parentFullName: response.data.data.parentName,
          parentEmail: response.data.data.parentEmail,
          parentPhoneNumber: response.data.data.parentPhone,
        });
      } catch (error) {
        console.error(error);
      }
    };
    fetchStudent();
  }, [activeURL]);

  if (!data || !data.data) {
    return <Loader />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "enrolledProgram") {
      setFormData((prev) => ({
        ...prev,
        enrolledProgram: value,
        enrolledSemester: "",
        studentSection: "",
      }));
      setSemester([]);
      setSections([]);
    } else if (name === "enrolledSemester") {
      setFormData((prev) => ({
        ...prev,
        enrolledSemester: value,
        studentSection: "",
      }));
      setSections([]);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await toast.promise(
        adminRequest.post(`${BASE_URL}/student/update`, {
          uuid: activeURL,
          ...formData,
        }),
        {
          pending: "Updating student...",
        },
      );

      toast.success(response.data.message || "Student updated successfully", {
        autoClose: 500,
        onClose: () => navigate(-1),
      });

      setFormData(initialFormData);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Update failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    {
      name: "fullName",
      label: "Student Name",
      type: "text",
      value: formData.fullName,
      onChange: handleChange,
    },
    {
      name: "email",
      label: "Student Email",
      type: "email",
      value: formData.email,
      onChange: handleChange,
    },
    {
      name: "phoneNumber",
      label: "Student Phone Number",
      type: "text",
      value: formData.phoneNumber,
      onChange: handleChange,
    },
    {
      name: "parentFullName",
      label: "Parent Name",
      type: "text",
      value: formData.parentFullName,
      onChange: handleChange,
    },
    {
      name: "parentEmail",
      label: "Parent Email",
      type: "email",
      value: formData.parentEmail,
      onChange: handleChange,
    },
    {
      name: "parentPhoneNumber",
      label: "Parent Phone Number",
      type: "text",
      value: formData.parentPhoneNumber,
      onChange: handleChange,
    },
    {
      name: "dateOfBirth",
      label: "Date Of Birth",
      type: "date",
      value: formData.dateOfBirth,
      onChange: handleChange,
    },
    {
      name: "address",
      label: "Address",
      type: "text",
      value: formData.address,
      onChange: handleChange,
    },
    {
      name: "batch",
      label: "Batch",
      type: "text",
      value: formData.batch,
      onChange: handleChange,
    },
    {
      name: "enrollmentSetupId",
      label: "Enrollment Batch",
      type: "select",
      value: formData.enrollmentSetupId,
      onChange: handleChange,
      options: [
        { label: "Select Batch", value: "" },
        ...enrollmentBatch.map((e) => ({
          label: e.enrollmentBatch,
          value: e.id,
        })),
      ],
    },
    {
      name: "enrolledProgram",
      label: "Enrolled Program",
      type: "select",
      value: formData.enrolledProgram,
      onChange: handleChange,
      options: [
        { label: "Select Program", value: "" },
        ...program.map((p) => ({ label: p.short_name, value: p.id })),
      ],
    },
    {
      name: "enrolledSemester",
      label: "Semester",
      type: "select",
      value: formData.enrolledSemester,
      onChange: handleChange,
      options: [
        { label: "Select Semester", value: "" },
        ...semester.map((s) => ({ label: s.semester, value: s.id })),
      ],
    },
    {
      name: "studentSection",
      label: "Section",
      type: "select",
      value: formData.studentSection,
      onChange: handleChange,
      options: [
        { label: "Select Section", value: "" },
        ...sections.map((s) => ({
          label: s.Section.name,
          value: s.Section.name,
        })),
      ],
    },
    {
      name: "enrollmentDate",
      label: "Enrolled Date",
      type: "date",
      value: formData.enrollmentDate,
      onChange: handleChange,
    },
  ];

  return (
    <div className="editStudentContainer">
      <CustomForm
        header="Edit Student"
        fields={fields}
        flexDirection="row"
        createButtonLabel="Update"
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
      <ToastContainer position="top-center" />
    </div>
  );
};

export default EditStudent;
